import { AuthenticationManager } from '@frontend/authentication-v2';
import { DefaultSecondaryButton } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { FormattedMessage } from 'react-intl';

import OverviewHeader from '../components/overview-header/overview-header';
import { TabFilter } from '../components/tab-filter/tab-filter.component';
import AuthenticationTab from './authentication-tab/authentication-tab.component';
import AuthorizationTab from './authorization-tab/authorization-tab.component';
import GeneralTab from './general-tab/general-tab.component';
import useProfile from './profile.controller';
import VersionBadge from './version-badge/version-badge.component';

export const Profile = () => {
    const viewProps = useProfile();

    return (
        <div
            key='profile'
            className='flex flex-col items-start justify-start gap-6 self-stretch'>
            <OverviewHeader
                title={
                    <FormattedMessage
                        id='profile.title'
                        defaultMessage='Profile'
                        description='Label for profile'
                    />
                }
                actions={[
                    <DefaultSecondaryButton
                        dataTestId='sign-out-button'
                        key='sign-out-button'
                        text={
                            <FormattedMessage
                                id='SettingsModal.SignOutButton'
                                description='The title in the sign out button in the settingsModal.'
                                defaultMessage='Sign out'
                            />
                        }
                        onClick={() => AuthenticationManager.getInstance().doLogout()}
                    />
                ]}
            />

            <div className='flex flex-col items-start justify-start gap-4 self-stretch'>
                <TabFilter
                    keys={['general', 'authentication', 'authorization']}
                    displayMap={
                        new Map([
                            ['general', CommonMessage.DETAIL.GENERAL],
                            [
                                'authentication',
                                <FormattedMessage
                                    key='profie.tabs.authentication'
                                    id='profile.tabs.authentication'
                                    defaultMessage='Authentication'
                                    description='Authentication tab in profile page'
                                />
                            ],
                            [
                                'authorization',
                                <FormattedMessage
                                    key='profie.tabs.authorization'
                                    id='profile.tabs.authorization'
                                    defaultMessage='Authorization'
                                    description='Authorization tab in profile page'
                                />
                            ]
                        ])
                    }
                    onChange={viewProps.setActiveTab}
                    useQueryParams={{ key: 'tab' }}
                />

                <PageContent
                    key={viewProps.activeTab}
                    {...viewProps}
                />
            </div>

            <div className='fixed right-0 bottom-4 left-0 z-10 flex justify-center'>
                <VersionBadge />
            </div>
        </div>
    );
};

const PageContent = (viewProps: ReturnType<typeof useProfile>) => {
    if (!viewProps.user) {
        return (
            <div className='flex w-full flex-col items-center justify-center gap-4 py-20'>
                <FormattedMessage
                    id='loading-user-profile'
                    defaultMessage='Loading user profile...'
                    description='Message shown while loading user profile'
                />
            </div>
        );
    }

    switch (viewProps.activeTab) {
        case 'general':
            return (
                <GeneralTab
                    user={viewProps.user}
                    privacyDocumentFile={viewProps.privacyDocumentFile}
                />
            );
        case 'authentication':
            return <AuthenticationTab userId={viewProps.user?.id} />;
        case 'authorization':
            return <AuthorizationTab userId={viewProps.user?.id} />;
    }
};
